import { sleep } from "common/Support";
import { logError } from "common/debug-print";
import { reloadBrowserWindow } from "./browser-reload";
import { setOriginOnline } from "./network-tracking";
import { setCloudRegion } from "common/function-region";

const deploymentVersion = (window as any).glideDeploymentVersion as string;
const updateHeaderTimeout = 10_000;
const updateBodyTimeout = 50_000;

export async function watchUpdates(): Promise<void> {
    const remoteVersionURL = document.location.origin;
    let lastUpdate = Date.now() - 10 * 60 * 1000;

    async function waitForever(seconds: number): Promise<true> {
        await sleep(seconds * 1000);
        return true;
    }

    let data: string | undefined;

    do {
        let fetched = false;
        try {
            if (Date.now() - lastUpdate < 1000 * 60 * 10) {
                // Only perform new version checks once every 10 minutes
                continue;
            }

            const abortController = new AbortController();

            const headerTimeoutHandle = setTimeout(() => abortController.abort(), updateHeaderTimeout);
            const response = await fetch(remoteVersionURL, {
                referrerPolicy: "origin",
                signal: abortController.signal,
            });

            const newRegion = response.headers.get("x-fly-region") ?? undefined;
            setCloudRegion(newRegion);

            fetched = true;
            clearTimeout(headerTimeoutHandle);

            if (!response.ok) {
                continue;
            }
            setOriginOnline(true);

            const bodyTimeoutHandle = setTimeout(() => abortController.abort(), updateBodyTimeout);
            data = await response.text();
            clearTimeout(bodyTimeoutHandle);
            if (!data.includes(`"${deploymentVersion}"`)) break;

            lastUpdate = Date.now();
        } catch (e: unknown) {
            logError(`Polling for application updates: ${e}`);
            if (!fetched) {
                setOriginOnline(false);
            }
        }
    } while (await waitForever(10));

    // The remote deployment version is different from our deployment version.
    // We should force a reload to update (this is necessary on iOS PWAs)
    // Chrome Android actually runs in the background, though, and sometimes just _fails_
    // to reload the browser window. Silently, too. That would make us wait for the next
    // 10 minute refresh check window, which we could also miss because we're still
    // in the background.
    //
    // Now, whenever we know we need to reload, we'll be somewhat beligerent about it:
    // try again every 10 seconds until you succeed. 1 second was too long, the Internet
    // is just bad.
    //
    // FIXME: This state should probably block app actions until the window reloads.
    do {
        reloadBrowserWindow("deployment versions differ", deploymentVersion, data);
    } while (await waitForever(10));
}
